import * as Sentry from '@sentry/react';
import { ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

function DefaultErrorFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
  const errorObj = error instanceof Error ? error : new Error(String(error));

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="max-w-md rounded-lg border border-destructive bg-card p-6 text-center">
        <h1 className="mb-4 text-2xl font-bold text-destructive">
          Something went wrong
        </h1>
        <p className="mb-4 text-muted-foreground">
          An unexpected error occurred. Please refresh the page.
        </p>
        <button
          onClick={() => {
            resetError();
            window.location.reload();
          }}
          className="rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
        >
          Refresh page
        </button>
        {errorObj && import.meta.env.DEV && (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-sm text-muted-foreground">
              Error details (development mode only)
            </summary>
            <pre className="mt-2 overflow-auto rounded bg-muted p-2 text-xs">
              {errorObj.toString()}
              {errorObj.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

export function ErrorBoundary({ children, fallback }: Props) {
  // Если передан кастомный fallback, оборачиваем его в функцию
  // Иначе используем DefaultErrorFallback
  const fallbackComponent: Sentry.FallbackRender = fallback
    ? () => <>{fallback}</>
    : DefaultErrorFallback;

  return (
    <Sentry.ErrorBoundary fallback={fallbackComponent} showDialog={false}>
      {children}
    </Sentry.ErrorBoundary>
  );
}
